import { useState } from 'react';
import { useComments, CommentData } from '../../features/blogs/hooks/useComments';
import authService from '../../features/auth/auth.service';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

interface CommentsSectionProps {
  blogSlug: string;
  postSlug: string;
}

// ── Single comment (recursive for replies) ────────────────────────────────────
function CommentItem({
  comment,
  onReply,
  onDelete,
  canDelete,
}: {
  comment: CommentData;
  onReply: (commentId: string, content: string) => Promise<boolean>;
  onDelete: (commentId: string) => Promise<boolean>;
  canDelete: (comment: CommentData) => boolean;
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    const ok = await onReply(comment.id, replyText.trim());
    if (ok) { setReplyText(''); setReplyOpen(false); }
    setSubmitting(false);
  };

  const initials = comment.author.name
    .split(' ')
    .map((n: string) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center select-none">
        {comment.author.avatarUrl ? (
          <img src={comment.author.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
        ) : initials}
      </div>

      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-foreground">{comment.author.name}</span>
          <span className="text-xs text-muted-foreground">{relativeTime(comment.createdAt)}</span>
        </div>

        {/* Content */}
        <p className="mt-1 text-sm text-foreground/90 whitespace-pre-wrap break-words">
          {comment.content}
        </p>

        {/* Actions */}
        <div className="mt-1.5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setReplyOpen(v => !v)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Reply
          </button>
          {canDelete(comment) && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="text-xs text-red-500 hover:text-red-700 transition-colors"
            >
              Delete
            </button>
          )}
        </div>

        {/* Reply box */}
        {replyOpen && (
          <div className="mt-2 flex gap-2">
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              rows={2}
              maxLength={1000}
              disabled={submitting}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={handleReply}
                disabled={submitting || !replyText.trim()}
                className="px-3 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50 transition-opacity"
              >
                {submitting ? '…' : 'Post'}
              </button>
              <button
                type="button"
                onClick={() => { setReplyOpen(false); setReplyText(''); }}
                className="px-3 py-1 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Nested replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 border-l border-border/40">
            {comment.replies.map(reply => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onReply={onReply}
                onDelete={onDelete}
                canDelete={canDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
export function CommentsSection({ blogSlug, postSlug }: CommentsSectionProps) {
  const {
    comments, pagination, loading, submitting, error,
    addComment, addReply, deleteComment, isAuthenticated,
    fetchComments,
  } = useComments(blogSlug, postSlug);

  const { user, isAdmin, isVerified } = useAuth();
  const [resent, setResent] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [postError, setPostError] = useState<string | null>(null);

  const canDelete = (comment: CommentData) =>
    isAdmin || comment.author._id === user?._id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPostError(null);
    const ok = await addComment(newComment.trim());
    if (ok) {
      setNewComment('');
    } else {
      setPostError('Failed to post comment. Please try again.');
    }
  };

  return (
    <section className="mt-10 pt-8 border-t-2 border-surface-variant" aria-label="Comments">
      <h2 className="text-lg font-semibold text-foreground mb-6">
        Comments
        {pagination.total > 0 && (
          <span className="ml-2 text-sm text-muted-foreground font-normal">({pagination.total})</span>
        )}
      </h2>

      {/* Comment form */}
      {isAuthenticated && !isVerified ? (
        <div className="mb-8 flex flex-col gap-3 rounded-xl border border-border bg-primary-fixed p-4">
          <p className="text-sm leading-relaxed text-foreground">
            <span className="font-semibold">Confirm your email to comment.</span> We sent a link to{' '}
            {user?.email ?? 'your address'} when you signed up — it only takes a click.
          </p>
          {resent ? (
            <p className="text-sm text-muted-foreground">A new link is on its way.</p>
          ) : (
            <button
              type="button"
              onClick={async () => {
                if (user?.email) await authService.resendVerification(user.email);
                setResent(true);
              }}
              className="self-start rounded-lg border border-primary px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              Send the link again
            </button>
          )}
        </div>
      ) : isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mb-8">
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            rows={3}
            maxLength={1000}
            disabled={submitting}
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">{newComment.length}/1000</span>
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              {submitting ? 'Posting…' : 'Post comment'}
            </button>
          </div>
          {postError && <p className="mt-1 text-xs text-red-500">{postError}</p>}
        </form>
      ) : (
        <p className="mb-8 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link> to leave a comment.
        </p>
      )}

      {/* Comment list */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-muted shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-muted rounded w-1/4" />
                <div className="h-3 bg-muted rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet. Be the first!</p>
      ) : (
        <div className="space-y-6">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={addReply}
              onDelete={deleteComment}
              canDelete={canDelete}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              type="button"
              onClick={() => fetchComments(page)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                ${page === pagination.page
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-muted-foreground hover:bg-muted'
                }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
